import * as Sentry from "@sentry/react";

const SENTRY_DSN = import.meta.env.VITE_SENTRY_FRONTEND_DSN;
const RELEASE = import.meta.env.VITE_APP_VERSION || "ticket-swiftly@1.0.0";
const ENVIRONMENT = import.meta.env.MODE || "development";

export function initSentry() {
  if (!SENTRY_DSN) {
    console.warn("[Sentry] No DSN configured — skipping initialization. Set VITE_SENTRY_FRONTEND_DSN.");
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    release: RELEASE,
    environment: ENVIRONMENT,

    // Performance Monitoring
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Capture 100% of transactions in dev, 20% in production
    tracesSampleRate: ENVIRONMENT === "production" ? 0.2 : 1.0,

    // Session Replay: 10% of sessions, 100% on error
    replaysSessionSampleRate: ENVIRONMENT === "production" ? 0.1 : 1.0,
    replaysOnErrorSampleRate: 1.0,

    // Filter out noisy errors
    beforeSend(event) {
      // Don't send events without a DSN in dev
      if (!SENTRY_DSN) return null;

      // Filter out browser extension errors
      if (event.exception?.values?.[0]?.stacktrace?.frames?.some(
        (frame) => frame.filename?.includes("extension://")
      )) {
        return null;
      }

      // Filter out HMR-related invalid hook call errors (dev-only transient issue)
      const errorMessage = event.exception?.values?.[0]?.value || "";
      if (
        errorMessage.includes("Cannot read properties of null (reading 'useContext')") ||
        errorMessage.includes("Cannot read properties of null (reading 'useState')") ||
        errorMessage.includes("Cannot read properties of null (reading 'useRef')")
      ) {
        return null;
      }

      return event;
    },

    // Add breadcrumbs for navigation
    beforeBreadcrumb(breadcrumb) {
      // Filter out noisy console breadcrumbs in production
      if (breadcrumb.category === "console" && 
          (breadcrumb.level === "debug" || breadcrumb.level === "info")) {
        return null;
      }
      return breadcrumb;
    },
  });
}

/**
 * Set user context for Sentry (call after login)
 */
export function setSentryUser(user: { id: string; email?: string; role?: string }) {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    role: user.role,
  });
}

/**
 * Clear user context (call on logout)
 */
export function clearSentryUser() {
  Sentry.setUser(null);
}

/**
 * Capture a custom error with optional context
 */
export function captureError(error: unknown, context?: Record<string, any>) {
  // Ensure we always pass a real Error instance to Sentry
  const realError = error instanceof Error
    ? error
    : new Error(
        typeof error === 'object' && error !== null && 'message' in error
          ? String((error as any).message)
          : String(error)
      );

  Sentry.withScope((scope) => {
    if (context) {
      // Strip non-serializable values to avoid Sentry noise
      const safeContext: Record<string, any> = {};
      for (const [key, value] of Object.entries(context)) {
        if (value !== undefined && typeof value !== 'function') {
          safeContext[key] = value;
        }
      }
      scope.setExtras(safeContext);
    }
    Sentry.captureException(realError);
  });
}

/**
 * Add a breadcrumb for tracking user flow
 */
export function addBreadcrumb(
  message: string,
  category: string,
  data?: Record<string, any>,
  level: Sentry.SeverityLevel = "info"
) {
  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level,
  });
}

/**
 * Capture checkout/payment errors with rich context
 */
export function capturePaymentError(
  error: Error,
  paymentContext: {
    eventId?: string;
    amount?: number;
    currency?: string;
    paymentMethod?: string;
    step?: string;
  }
) {
  Sentry.withScope((scope) => {
    scope.setTag("transaction.type", "payment");
    scope.setTag("payment.step", paymentContext.step || "unknown");
    scope.setExtras(paymentContext);
    scope.setLevel("error");
    Sentry.captureException(error);
  });
}

/**
 * Capture checkout failure
 */
export function captureCheckoutFailure(
  error: Error,
  checkoutContext: {
    plan?: string;
    cycle?: string;
    userId?: string;
    step?: string;
  }
) {
  Sentry.withScope((scope) => {
    scope.setTag("transaction.type", "checkout");
    scope.setTag("checkout.step", checkoutContext.step || "unknown");
    scope.setExtras(checkoutContext);
    scope.setLevel("error");
    scope.setFingerprint(["checkout-failure", checkoutContext.step || "unknown"]);
    Sentry.captureException(error);
  });
}

/**
 * Trigger a test error (for verification)
 */
export function triggerTestError() {
  try {
    throw new Error("[Sentry Test] This is a test error from Ticket Swiftly frontend");
  } catch (e) {
    Sentry.captureException(e);
    console.log("[Sentry] Test error sent — check your Sentry dashboard.");
  }
}

export { Sentry };
